import React, {useEffect} from 'react';
import {withRouter} from "react-router";
import {Translation, getI18n} from 'react-i18next';
import {Table} from 'antd';

import ListActions from '../../../elements/components/ListActions';
import {getColumnSearchProps, getColumnDateRangeProps} from '../../../elements/components/TableColumnFilters';
import {PriorityTagEmail, SendStatusTag} from '../../../elements/components/Tags';
import {pathTo} from '../../../Routes';
import Format from '../../../../../lib/Format';
import QueryString from '../../../../../lib/QueryString';
import Logger from '../../../../../lib/Logger';

const EmailsList = ({component, page, limit, order, filter, total, load, remove, history, ...props}) => {

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters, dataIndex) => {
    clearFilters();
    delete filter[dataIndex];
    history.push(QueryString.append(props.location.pathname, {order, ...filter}));
  };

  const columns = [
    {
      title: getI18n().t('table_header_id'),
      dataIndex: 'id',
      key: 'id',
      defaultSortOrder: order === 'id.asc' ? 'ascend' : (order === 'id.desc' ? 'descend' : null),
      sorter: true,
      ...getColumnSearchProps('id', handleSearch, handleReset, getI18n().t('table_header_id'), filter),
    },
    {
      title: getI18n().t('emails_accepted'),
      dataIndex: 'accepted',
      key: 'accepted',
      defaultSortOrder: order === 'accepted.asc' ? 'ascend' : (order === 'accepted.desc' ? 'descend' : null),
      sorter: true,
      ...getColumnSearchProps('accepted', handleSearch, handleReset, getI18n().t('emails_accepted'), filter),
    },
    {
      title: getI18n().t('emails_rejected'),
      dataIndex: 'rejected',
      key: 'rejected',
      defaultSortOrder: order === 'rejected.asc' ? 'ascend' : (order === 'rejected.desc' ? 'descend' : null),
      sorter: true,
      ...getColumnSearchProps('rejected', handleSearch, handleReset, getI18n().t('emails_rejected'), filter),
    },
    {
      title: getI18n().t('emails_attempts'),
      dataIndex: 'attempts',
      key: 'attempts',
      defaultSortOrder: order === 'attempts.asc' ? 'ascend' : (order === 'attempts.desc' ? 'descend' : null),
      sorter: true,
      ...getColumnSearchProps('attempts', handleSearch, handleReset, getI18n().t('emails_attempts'), filter),
    },
    {
      title: getI18n().t('emails_priority'),
      dataIndex: 'priority',
      key: 'priority',
      filters: [
        {text: getI18n().t('email_priority_now'), value: 0},
        {text: getI18n().t('email_priority_high'), value: 1},
        {text: getI18n().t('email_priority_medium'), value: 2},
        {text: getI18n().t('email_priority_low'), value: 3},
      ],
      filteredValue: 'priority' in filter ? filter['priority'].split(',') : null,
      render: priority => <PriorityTagEmail priority={priority} />
    },
    {
      title: getI18n().t('emails_send_status'),
      dataIndex: 'send_status',
      key: 'send_status',
      filters: [
        {text: getI18n().t('email_send_status_queued'), value: 1},
        {text: getI18n().t('email_send_status_processing'), value: 2},
        {text: getI18n().t('email_send_status_complete'), value: 3},
        {text: getI18n().t('email_send_status_failed'), value: 4},
      ],
      filteredValue: 'send_status' in filter ? filter['send_status'].split(',') : null,
      render: send_status => <SendStatusTag send_status={send_status} />
    },
    {
      title: getI18n().t('emails_template'),
      dataIndex: 'template',
      key: 'template',
      defaultSortOrder: order === 'template.asc' ? 'ascend' : (order === 'template.desc' ? 'descend' : null),
      sorter: true,
      ...getColumnSearchProps('template', handleSearch, handleReset, getI18n().t('emails_template'), filter),
    },
    {
      title: getI18n().t('emails_recipients'),
      dataIndex: 'recipients',
      key: 'recipients',
      render: recipients => (recipients && Array.isArray(recipients) ? recipients.join(', ') : ''),
    },
    {
      title: getI18n().t('emails_last_attempt_at'),
      dataIndex: 'last_attempt_at',
      key: 'last_attempt_at',
      render: date => Format.date(date),
      defaultSortOrder: order === 'last_attempt_at.asc' ? 'ascend' : (order === 'last_attempt_at.desc' ? 'descend' : null),
      sorter: true,
      ...getColumnDateRangeProps('last_attempt_at', handleSearch, handleReset, filter),
    },
    {
      title: getI18n().t('emails_queued_at'),
      dataIndex: 'queued_at',
      key: 'queued_at',
      render: date => Format.date(date),
      defaultSortOrder: order === 'queued_at.asc' ? 'ascend' : (order === 'queued_at.desc' ? 'descend' : null),
      sorter: true,
      ...getColumnDateRangeProps('queued_at', handleSearch, handleReset, filter),
    },
    {
      title: getI18n().t('table_header_updated_at'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: date => Format.date(date),
      defaultSortOrder: order === 'updated_at.asc' ? 'ascend' : (order === 'updated_at.desc' ? 'descend' : null),
      sorter: true,
      ...getColumnDateRangeProps('updated_at', handleSearch, handleReset, filter),
    },
    {
      title: getI18n().t('table_header_created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: date => Format.date(date),
      defaultSortOrder: order === 'created_at.asc' ? 'ascend' : (order === 'created_at.desc' ? 'descend' : null),
      sorter: true,
      ...getColumnDateRangeProps('created_at', handleSearch, handleReset, filter),
    },
    {
      title: getI18n().t('table_header_actions'),
      dataIndex: 'id',
      key: 'actions',
      render: id =>
        <ListActions
          id={id}
          page={page}
          limit={limit}
          order={order}
          filter={filter}
          load={load.bind(this)}
          remove={remove.bind(this)}
          editScreen="EmailEditScreen"
        />,
    },
  ];

  const pagination = {
    current: page,
    pageSize: limit,
    total: total,
    showSizeChanger: false,
    position: ['bottomCenter'],
  }

  const handleTableChange = (pagination, filters, sorter) => {

    let path = props.location.pathname;
    const params = {};

    // handle pagination
    if ('current' in pagination && pagination['current']) {
      path = pathTo(component, {page: pagination['current']});
    }

    // handle sorting
    if ('field' in sorter && 'order' in sorter) {
      if (sorter['field'] && sorter['order']) {
        const order = sorter['field'] + '.' + (sorter['order'] === 'ascend' ? 'asc' : 'desc');
        params['order'] = order;
      }
    }

    // handle filters
    if (filters) {
      for (const key in filters) {
        if (filters[key]) {
          params[key] = filters[key].join(',');
        }
      }
    }

    history.push(QueryString.append(path, params));
  }

  const filterString = JSON.stringify(filter);
  useEffect(() => {
    load(page, limit, order, JSON.parse(filterString));
  }, [page, limit, order, filterString, load]);

  return (
    <Translation>{(t) => 
      <Table
        dataSource={props.list}
        columns={columns}
        pagination={page === 1 && total <= limit  ? false : pagination}
        loading={props.isLoading}
        scroll={{x: true}}
        onChange={handleTableChange}
      />

    }</Translation>
  );
}

export default withRouter(EmailsList);

Logger.log('silly', `EmailsList loaded.`);
